/**
 * Agent API Client
 */

import { api } from '@/lib/api-client';
import { getStoredToken } from '@/lib/auth-client';
import type {
  Agent,
  AgentResponse,
  AgentListResponse,
  CreateAgentInput,
  UpdateAgentInput,
  AgentInput,
  AgentListOptions,
  ExecutionResponse,
  ExecutionDetailResponse,
  ExecutionListResponse,
  ExecutionListOptions,
  SessionListResponse,
  SessionDetailResponse,
  MessageResponse,
  AgentEvent,
  TemplateCategory,
  TemplateResponse,
  TemplateListResponse,
  Crew,
  CrewResponse,
  CrewListResponse,
  CrewExecutionResponse,
  CreateCrewInput,
  UpdateCrewInput,
  CrewProcess,
} from './types';

const AGENTS_BASE = '/api/v1/agents';

// ============================================
// Agent CRUD
// ============================================

/**
 * Create a new agent
 */
export async function createAgent(input: CreateAgentInput): Promise<AgentResponse> {
  return api.post<AgentResponse>(AGENTS_BASE, input);
}

/**
 * List agents with filtering and pagination
 */
export async function listAgents(options: AgentListOptions = {}): Promise<AgentListResponse> {
  const params: Record<string, string> = {};

  if (options.type) params.type = options.type;
  if (options.active !== undefined) params.active = String(options.active);
  if (options.search) params.search = options.search;
  if (options.limit) params.limit = String(options.limit);
  if (options.offset) params.offset = String(options.offset);

  return api.get<AgentListResponse>(AGENTS_BASE, { params });
}

/**
 * Get agent details
 */
export async function getAgent(agentId: string): Promise<AgentResponse> {
  return api.get<AgentResponse>(`${AGENTS_BASE}/${agentId}`);
}

/**
 * Update an agent
 */
export async function updateAgent(
  agentId: string,
  input: UpdateAgentInput
): Promise<AgentResponse> {
  return api.put<AgentResponse>(`${AGENTS_BASE}/${agentId}`, input);
}

/**
 * Delete an agent
 */
export async function deleteAgent(agentId: string): Promise<MessageResponse> {
  return api.delete<MessageResponse>(`${AGENTS_BASE}/${agentId}`);
}

// ============================================
// Agent Execution
// ============================================

/**
 * Execute an agent (non-streaming)
 */
export async function executeAgent(
  agentId: string,
  input: AgentInput
): Promise<ExecutionResponse> {
  return api.post<ExecutionResponse>(`${AGENTS_BASE}/${agentId}/execute`, input);
}

/**
 * Stream agent execution via SSE
 * Returns an async generator that yields AgentEvents
 */
export async function* streamAgent(
  agentId: string,
  input: AgentInput
): AsyncGenerator<AgentEvent> {
  const token = getStoredToken();
  const response = await fetch(`${AGENTS_BASE}/${agentId}/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw new Error(`Stream request failed: ${response.statusText}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('No response body');
  }

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Process complete SSE events
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6).trim();
          if (data) {
            try {
              const event = JSON.parse(data) as AgentEvent;
              yield event;
            } catch (e) {
              console.warn('Failed to parse SSE event:', data);
            }
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * Create an EventSource for streaming (alternative approach)
 */
export function createAgentEventSource(
  agentId: string,
  message: string,
  sessionId?: string
): EventSource {
  const params = new URLSearchParams({ message });
  if (sessionId) params.append('sessionId', sessionId);

  return new EventSource(`${AGENTS_BASE}/${agentId}/stream?${params.toString()}`);
}

// ============================================
// Execution History
// ============================================

/**
 * List executions for an agent
 */
export async function listExecutions(
  agentId: string,
  options: ExecutionListOptions = {}
): Promise<ExecutionListResponse> {
  const params: Record<string, string> = {};

  if (options.status) params.status = options.status;
  if (options.limit) params.limit = String(options.limit);
  if (options.offset) params.offset = String(options.offset);

  return api.get<ExecutionListResponse>(`${AGENTS_BASE}/${agentId}/executions`, { params });
}

/**
 * Get execution details
 */
export async function getExecution(executionId: string): Promise<ExecutionDetailResponse> {
  return api.get<ExecutionDetailResponse>(`${AGENTS_BASE}/executions/${executionId}`);
}

/**
 * Cancel a running execution
 */
export async function cancelExecution(executionId: string): Promise<MessageResponse> {
  return api.post<MessageResponse>(`${AGENTS_BASE}/executions/${executionId}/cancel`);
}

// ============================================
// Session Management
// ============================================

/**
 * List sessions for an agent
 */
export async function listSessions(
  agentId: string,
  options: { limit?: number; offset?: number } = {}
): Promise<SessionListResponse> {
  const params: Record<string, string> = {};

  if (options.limit) params.limit = String(options.limit);
  if (options.offset) params.offset = String(options.offset);

  return api.get<SessionListResponse>(`${AGENTS_BASE}/${agentId}/sessions`, { params });
}

/**
 * Get session details with memory
 */
export async function getSession(
  agentId: string,
  sessionId: string
): Promise<SessionDetailResponse> {
  return api.get<SessionDetailResponse>(`${AGENTS_BASE}/${agentId}/sessions/${sessionId}`);
}

/**
 * Clear session memory
 */
export async function clearSession(
  agentId: string,
  sessionId: string
): Promise<MessageResponse> {
  return api.delete<MessageResponse>(`${AGENTS_BASE}/${agentId}/sessions/${sessionId}`);
}

// ============================================
// Agent Generation
// ============================================

export interface GeneratedConfig {
  name: string;
  description: string;
  agentType: string;
  config: import('./types').AgentConfig;
  reasoning: string;
  suggestedImprovements?: string[];
}

export interface GenerateAgentResponse {
  success: boolean;
  data: GeneratedConfig;
}

export interface RefineAgentResponse {
  success: boolean;
  data: GeneratedConfig;
}

export interface ValidateAgentResponse {
  success: boolean;
  data: {
    valid: boolean;
    errors: Array<{ path: string[]; message: string }>;
    canvasErrors?: Array<{ path: string[]; message: string }>;
  };
}

export interface CompileCanvasResponse {
  success: boolean;
  data: unknown | null;
  errors: Array<{ nodeId?: string; code: string; message: string }>;
  warnings: Array<{ nodeId?: string; code: string; message: string }>;
}

/**
 * Generate agent configuration from natural language description
 */
export async function generateAgentConfig(
  description: string,
  context?: {
    existingAgents?: string[];
    availableTools?: string[];
    preferredProvider?: string;
  }
): Promise<GenerateAgentResponse> {
  return api.post<GenerateAgentResponse>(`${AGENTS_BASE}/generate`, {
    description,
    context,
  });
}

/**
 * Refine an existing agent configuration based on feedback
 */
export async function refineAgentConfig(
  currentConfig: GeneratedConfig,
  feedback: string
): Promise<RefineAgentResponse> {
  return api.post<RefineAgentResponse>(`${AGENTS_BASE}/refine`, {
    currentConfig,
    feedback,
  });
}

/**
 * Validate agent configuration
 */
export async function validateAgentConfig(
  config: import('./types').CreateAgentInput,
  canvasData?: import('./types').CanvasData
): Promise<ValidateAgentResponse> {
  return api.post<ValidateAgentResponse>(`${AGENTS_BASE}/validate`, {
    config,
    canvasData,
  });
}

/**
 * Compile canvas workflow to agent configuration
 */
export async function compileCanvas(
  canvasData: import('./types').CanvasData
): Promise<CompileCanvasResponse> {
  return api.post<CompileCanvasResponse>(`${AGENTS_BASE}/compile-canvas`, {
    canvasData,
  });
}

// ============================================
// Debug Execution
// ============================================

export interface DebugSession {
  id: string;
  agent_id: string;
  user_id: string;
  status: 'active' | 'paused' | 'completed' | 'error';
  current_node_id: string | null;
  variables: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface DebugSessionResponse {
  success: boolean;
  data: DebugSession;
}

export interface DebugStepResponse {
  success: boolean;
  data: {
    nodeId: string;
    output: unknown;
    variables: Record<string, unknown>;
    completed: boolean;
  };
}

export interface PreviewResult {
  nodeId: string;
  nodeType: string;
  input: unknown;
  output: unknown;
  outputType: {
    type: string;
    shape?: string;
  };
  duration?: number;
  error?: string;
}

export interface PreviewResponse {
  success: boolean;
  nodeResults: Record<string, PreviewResult>;
  totalDuration: number;
}

/**
 * Start a debug session
 */
export async function startDebugSession(
  agentId: string,
  input: unknown
): Promise<DebugSessionResponse> {
  return api.post<DebugSessionResponse>(`${AGENTS_BASE}/${agentId}/debug`, { input });
}

/**
 * Step through debug execution
 */
export async function stepDebug(sessionId: string): Promise<DebugStepResponse> {
  return api.post<DebugStepResponse>(`${AGENTS_BASE}/debug/${sessionId}/step`);
}

/**
 * Continue debug execution to next breakpoint
 */
export async function continueDebug(sessionId: string): Promise<DebugStepResponse> {
  return api.post<DebugStepResponse>(`${AGENTS_BASE}/debug/${sessionId}/continue`);
}

/**
 * Pause debug execution
 */
export async function pauseDebug(sessionId: string): Promise<DebugSessionResponse> {
  return api.post<DebugSessionResponse>(`${AGENTS_BASE}/debug/${sessionId}/pause`);
}

/**
 * Stop debug session
 */
export async function stopDebug(sessionId: string): Promise<MessageResponse> {
  return api.post<MessageResponse>(`${AGENTS_BASE}/debug/${sessionId}/stop`);
}

/**
 * Modify a variable during debug
 */
export async function modifyDebugVariable(
  sessionId: string,
  path: string,
  value: unknown
): Promise<DebugSessionResponse> {
  return api.post<DebugSessionResponse>(`${AGENTS_BASE}/debug/${sessionId}/variables`, {
    path,
    value,
  });
}

/**
 * Run data preview through workflow
 */
export async function runPreview(
  agentId: string,
  input: unknown
): Promise<PreviewResponse> {
  return api.post<PreviewResponse>(`${AGENTS_BASE}/${agentId}/preview`, { input });
}

// ============================================
// Version History
// ============================================

export interface AgentVersion {
  id: string;
  agent_id: string;
  version: number;
  change_summary: string | null;
  change_type: 'create' | 'update' | 'publish' | 'restore';
  created_by: string;
  created_at: string;
  canvas_data?: unknown;
  config_snapshot?: unknown;
}

export interface VersionListResponse {
  success: boolean;
  data: AgentVersion[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface VersionResponse {
  success: boolean;
  data: AgentVersion;
}

export interface VersionDiffResponse {
  success: boolean;
  data: {
    version1: number;
    version2: number;
    diff: {
      added: string[];
      removed: string[];
      modified: string[];
    };
  };
}

/**
 * List agent versions
 */
export async function listVersions(
  agentId: string,
  options: { limit?: number; offset?: number } = {}
): Promise<VersionListResponse> {
  const params: Record<string, string> = {};
  if (options.limit) params.limit = String(options.limit);
  if (options.offset) params.offset = String(options.offset);

  return api.get<VersionListResponse>(`${AGENTS_BASE}/${agentId}/versions`, { params });
}

/**
 * Get specific version
 */
export async function getVersion(
  agentId: string,
  version: number
): Promise<VersionResponse> {
  return api.get<VersionResponse>(`${AGENTS_BASE}/${agentId}/versions/${version}`);
}

/**
 * Restore to a specific version
 */
export async function restoreVersion(
  agentId: string,
  version: number
): Promise<AgentResponse> {
  return api.post<AgentResponse>(`${AGENTS_BASE}/${agentId}/versions/${version}/restore`);
}

/**
 * Compare two versions
 */
export async function compareVersions(
  agentId: string,
  v1: number,
  v2: number
): Promise<VersionDiffResponse> {
  return api.get<VersionDiffResponse>(
    `${AGENTS_BASE}/${agentId}/versions/compare?v1=${v1}&v2=${v2}`
  );
}

// ============================================
// Templates
// ============================================

const TEMPLATES_BASE = '/api/v1/templates';

export interface TemplateListOptions {
  category?: TemplateCategory;
  search?: string;
  official?: boolean;
  featured?: boolean;
  limit?: number;
  offset?: number;
}

/**
 * List templates with filtering
 */
export async function listTemplates(
  options: TemplateListOptions = {}
): Promise<TemplateListResponse> {
  const params: Record<string, string> = {};

  if (options.category) params.category = options.category;
  if (options.search) params.search = options.search;
  if (options.official !== undefined) params.official = String(options.official);
  if (options.featured !== undefined) params.featured = String(options.featured);
  if (options.limit) params.limit = String(options.limit);
  if (options.offset) params.offset = String(options.offset);

  return api.get<TemplateListResponse>(TEMPLATES_BASE, { params });
}

/**
 * Get template details
 */
export async function getTemplate(templateId: string): Promise<TemplateResponse> {
  return api.get<TemplateResponse>(`${TEMPLATES_BASE}/${templateId}`);
}

/**
 * Create agent from template
 */
export async function useTemplate(
  templateId: string,
  overrides?: { name?: string; description?: string }
): Promise<AgentResponse> {
  return api.post<AgentResponse>(`${TEMPLATES_BASE}/${templateId}/use`, overrides || {});
}

// ============================================
// Crews
// ============================================

const CREWS_BASE = '/api/v1/crews';

export interface CrewListOptions {
  processType?: CrewProcess;
  search?: string;
  limit?: number;
  offset?: number;
}

/**
 * Create a new crew
 */
export async function createCrew(input: CreateCrewInput): Promise<CrewResponse> {
  return api.post<CrewResponse>(CREWS_BASE, input);
}

/**
 * List crews
 */
export async function listCrews(options: CrewListOptions = {}): Promise<CrewListResponse> {
  const params: Record<string, string> = {};

  if (options.processType) params.processType = options.processType;
  if (options.search) params.search = options.search;
  if (options.limit) params.limit = String(options.limit);
  if (options.offset) params.offset = String(options.offset);

  return api.get<CrewListResponse>(CREWS_BASE, { params });
}

/**
 * Get crew details
 */
export async function getCrew(crewId: string): Promise<CrewResponse> {
  return api.get<CrewResponse>(`${CREWS_BASE}/${crewId}`);
}

/**
 * Update a crew
 */
export async function updateCrew(crewId: string, input: UpdateCrewInput): Promise<CrewResponse> {
  return api.put<CrewResponse>(`${CREWS_BASE}/${crewId}`, input);
}

/**
 * Delete a crew
 */
export async function deleteCrew(crewId: string): Promise<MessageResponse> {
  return api.delete<MessageResponse>(`${CREWS_BASE}/${crewId}`);
}

/**
 * Execute a crew (non-streaming)
 */
export async function executeCrew(
  crewId: string,
  input: Record<string, unknown>
): Promise<CrewExecutionResponse> {
  return api.post<CrewExecutionResponse>(`${CREWS_BASE}/${crewId}/execute`, { input });
}

/**
 * Validate crew configuration
 */
export async function validateCrew(
  crewId: string
): Promise<{ success: boolean; data: { valid: boolean; errors: string[] } }> {
  return api.post(`${CREWS_BASE}/${crewId}/validate`);
}

// ============================================
// Tools Discovery
// ============================================

const TOOLS_BASE = '/api/v1/tools';

export interface Tool {
  name: string;
  description: string;
  provider?: string;
  category: string;
  isPopular: boolean;
  isDestructive: boolean;
  tags: string[];
}

export interface ToolDetails extends Tool {
  inputSchema: unknown;
  method?: string;
  endpoint?: string;
}

export interface ToolProvider {
  name: string;
  displayName: string;
  toolCount: number;
  isPopular: boolean;
}

export interface ToolCategory {
  name: string;
  toolCount: number;
}

export interface ToolListOptions {
  query?: string;
  provider?: string;
  category?: string;
  popular?: boolean;
  limit?: number;
  offset?: number;
}

export interface ToolListResponse {
  success: boolean;
  data: Tool[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface ToolProvidersResponse {
  success: boolean;
  data: ToolProvider[];
}

export interface ToolCategoriesResponse {
  success: boolean;
  data: ToolCategory[];
}

export interface ToolDetailsResponse {
  success: boolean;
  data: ToolDetails;
}

/**
 * Search and list available MCP tools
 */
export async function listTools(options: ToolListOptions = {}): Promise<ToolListResponse> {
  const params: Record<string, string> = {};

  if (options.query) params.query = options.query;
  if (options.provider) params.provider = options.provider;
  if (options.category) params.category = options.category;
  if (options.popular !== undefined) params.popular = String(options.popular);
  if (options.limit) params.limit = String(options.limit);
  if (options.offset) params.offset = String(options.offset);

  return api.get<ToolListResponse>(TOOLS_BASE, { params });
}

/**
 * List available tool providers
 */
export async function listToolProviders(popular?: boolean): Promise<ToolProvidersResponse> {
  const params: Record<string, string> = {};
  if (popular !== undefined) params.popular = String(popular);

  return api.get<ToolProvidersResponse>(`${TOOLS_BASE}/providers`, { params });
}

/**
 * List tool categories
 */
export async function listToolCategories(): Promise<ToolCategoriesResponse> {
  return api.get<ToolCategoriesResponse>(`${TOOLS_BASE}/categories`);
}

/**
 * Get tool details including schema
 */
export async function getToolDetails(toolName: string): Promise<ToolDetailsResponse> {
  return api.get<ToolDetailsResponse>(`${TOOLS_BASE}/${toolName}`);
}

// ============================================
// Export all functions
// ============================================

export default {
  // CRUD
  createAgent,
  listAgents,
  getAgent,
  updateAgent,
  deleteAgent,
  // Execution
  executeAgent,
  streamAgent,
  createAgentEventSource,
  // History
  listExecutions,
  getExecution,
  cancelExecution,
  // Sessions
  listSessions,
  getSession,
  clearSession,
  // Generation
  generateAgentConfig,
  refineAgentConfig,
  validateAgentConfig,
  compileCanvas,
  // Debug
  startDebugSession,
  stepDebug,
  continueDebug,
  pauseDebug,
  stopDebug,
  modifyDebugVariable,
  runPreview,
  // Versions
  listVersions,
  getVersion,
  restoreVersion,
  compareVersions,
  // Templates
  listTemplates,
  getTemplate,
  useTemplate,
  // Crews
  createCrew,
  listCrews,
  getCrew,
  updateCrew,
  deleteCrew,
  executeCrew,
  validateCrew,
  // Tools Discovery
  listTools,
  listToolProviders,
  listToolCategories,
  getToolDetails,
};
